import { Border, Spacings } from "@ptml/design-system";
import styled, { css } from "styled-components";
import {
    RobotSignalShapeList,
    RobotSignalVariantList,
    type RobotSignalCardVariant,
    type RobotSignalGradientDirection,
    type RobotSignalIconBadgeStyleProps,
    type RobotSignalVariant,
} from "./RobotSignal.types";
import {
    getRobotSignalBorderGradient,
    RobotSignalVariantTokens,
} from "./RobotSignal.tokens";

/** Shared disabled-state override so it isn't duplicated per styled-component below. */
const getDisabledStyles = (variant: RobotSignalVariant, disabled?: boolean) =>
    disabled &&
    css`
        background: ${RobotSignalVariantTokens[variant].disabledBackground};
        cursor: not-allowed;
    `;

export const RobotSignalCard = styled.div<{
    $variant?: RobotSignalCardVariant;
    $disabled?: boolean;
    $gradientDirection?: RobotSignalGradientDirection;
}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${Spacings.xxsmall};
    border: 1px solid transparent;
    border-radius: ${Border.Radius.Size.medium};
    min-width: 184px;
    background: ${({
        $variant = RobotSignalVariantList.GRADIENT_CARD,
        $gradientDirection,
    }) => RobotSignalVariantTokens[$variant].background($gradientDirection)};

    ${({ $variant = RobotSignalVariantList.GRADIENT_CARD, $disabled }) =>
        getDisabledStyles($variant, $disabled)}
`;

const robotSignalIconBadgeStyles = css<RobotSignalIconBadgeStyleProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${Spacings.xxxsmall};
    border: 1px solid transparent;
    border-radius: ${({ $shape = RobotSignalShapeList.SQUARE }) =>
        $shape === RobotSignalShapeList.CIRCLE
            ? Border.Radius.Size.halved
            : Border.Radius.Size[4]};
    background: ${({
        $variant = RobotSignalVariantList.GRADIENT_OUTLINE,
        $gradientDirection,
    }) => RobotSignalVariantTokens[$variant].background($gradientDirection)};

    ${({ $variant = RobotSignalVariantList.GRADIENT_OUTLINE, $disabled }) =>
        getDisabledStyles($variant, $disabled)}
`;

/** Standalone, non-interactive icon badge - e.g. decorative use inside a header. */
export const RobotSignalIconBadge = styled.div<RobotSignalIconBadgeStyleProps>`
    ${robotSignalIconBadgeStyles}
`;

/** Same badge, rendered as a real `<button>` for interactive/toggle usages. */
export const RobotSignalIconButton = styled.button<RobotSignalIconBadgeStyleProps>`
    ${robotSignalIconBadgeStyles}
    gap: ${Spacings.xxxsmall};
    appearance: none;
    font: inherit;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    line-height: normal !important;
    flex-shrink: 0;
    white-space: nowrap;
`;

const GRADIENT_BORDER_WIDTH = "1px";

/** Transparent interior so a wrapped element's own background shows through the gradient ring. */
export const GradientBorderWrapper = styled.div<{
    $gradientDirection?: RobotSignalGradientDirection;
}>`
    display: inline-flex;
    border: ${GRADIENT_BORDER_WIDTH} solid transparent;
    border-radius: calc(${Border.Radius.Size["6"]});
    background:
        linear-gradient(transparent, transparent) padding-box,
        ${({ $gradientDirection }) =>
                getRobotSignalBorderGradient($gradientDirection)}
            border-box;
`;
